import URI from 'urijs';
import { PartCatalogueFilterColumn } from '@/entities/part-catalogue/PartCatalogueFilterColumn';
import { PartCatalogueEntry } from '@/entities/part-catalogue/PartCatalogueEntry';
import { PartCatalogueAssemblyItemDetail } from '@/entities/part-catalogue/PartCatalogueAssemblyItemDetail';
import { apiClient } from './api-client';
import { isNullOrWhitespace } from '@/helper/isNullOrWhitespace';
import { PartCatalogueSearchPage } from '@/entities/part-catalogue/PartCatalogueSearchPage';
import { PartCatalogueTree } from '@/entities/part-catalogue/PartCatalogueTree';

/**
 * Service to retrieve part catalogue data.
 */
export class PartCatalogueService {
    private get baseUri(): URI {
        return new URI('/api/part-catalogue');
    }

    public async getEntry(entryId: number): Promise<PartCatalogueEntry> {
        const entryUri = this.baseUri.segmentCoded('entries').segmentCoded(entryId.toString());

        const res = await apiClient.get<PartCatalogueEntry>(entryUri.toString());

        return res.data;
    }

    public async getItemsOfEntry(assemblyId: string): Promise<PartCatalogueAssemblyItemDetail[]> {
        const entryUri = this.baseUri.segmentCoded('details').addQuery('id', assemblyId);

        const res = await apiClient.get<PartCatalogueAssemblyItemDetail[]>(entryUri.toString(), {
            method: 'GET',
        });

        return res.data;
    }

    public async getFilters(selectedFilterId: number | null): Promise<PartCatalogueFilterColumn[]> {
        let selectFilterUri = this.baseUri.segmentCoded('filters').segmentCoded('select');

        if (selectedFilterId != null) {
            selectFilterUri = selectFilterUri.segmentCoded(selectedFilterId.toString());
        }

        const res = await apiClient.get<PartCatalogueFilterColumn[]>(selectFilterUri.toString());

        return res.data;
    }

    /**
     * Search for the given term.
     * @param searchTerm term that is searched for
     * @param brand an optional filter for a specific brand
     * @param pageNumber the page that should be fetched
     * @param numberOfResultsPerPage the number of results that should be fetched per page
     */
    public async getSearchResults(
        searchTerm: string,
        brand: string,
        pageNumber: number,
        numberOfResultsPerPage: number
    ): Promise<PartCatalogueSearchPage> {
        const searchParams = {
            searchTerm,
            brand: isNullOrWhitespace(brand) ? undefined : brand,
            pageNumber: pageNumber < 1 ? 1 : pageNumber,
            numberOfResultsPerPage: numberOfResultsPerPage < 1 ? 12 : numberOfResultsPerPage,
        };

        const res = await apiClient.get<PartCatalogueSearchPage>(
            this.baseUri
                .segmentCoded('search')
                .addQuery(searchParams)
                .toString()
        );

        return res.data;
    }

    public getDrawingUrl(id: string): string {
        return this.baseUri
            .segment('getDrawing')
            .addQuery('id', id)
            .toString();
    }

    public getGroupThumbnailUrl(id: string): string {
        return this.baseUri
            .segment('getThumbnail')
            .segment('group')
            .addQuery('id', id)
            .toString();
    }

    public getProjectThumbnailUrl(id: string): string {
        return this.baseUri
            .segment('getThumbnail')
            .segment('project')
            .addQuery('id', id)
            .toString();
    }

    public getAssemblyThumbnailInLowQualityUrl(id: string): string {
        return this.baseUri
            .segment('getThumbnail')
            .segment('assembly')
            .segment('lq')
            .addQuery('id', id)
            .toString();
    }

    public getTreeNodeById(tree: PartCatalogueTree, id: string): PartCatalogueTree | null {
        if (tree.treeId === id) {
            return tree;
        }

        for (const child of tree.children) {
            const t = this.getTreeNodeById(child, id);
            if (t) {
                return t;
            }
        }

        return null;
    }
}
